/**
 * Lightweight index for the homepage's guide preview cards.
 *
 * Deliberately NOT derived from GUIDES (./seo/guides.ts) — that carries the
 * full body content for every guide, and vite.config.ts manually chunks it
 * into a separate `content` bundle so the /guides pages can cache it
 * independently of app code. Home.tsx used to import it directly just to read
 * a few guide titles and a count, which pulled that entire chunk into the
 * homepage's initial JS. This file is the ~2 KB the homepage actually needs.
 * It deliberately lives OUTSIDE src/data/seo/ (one directory up) — the
 * manualChunks rule matches on that path, so a file placed inside it would be
 * merged back into the same `content` chunk it exists to avoid.
 *
 * Kept in sync with the real data by homeIndex.test.ts, which fails if a
 * featured guide's slug, title or description diverges from GUIDES, or if the
 * count does.
 */

export const GUIDE_COUNT = 15

/*
 * Hand-picked, not the first N guides. The homepage is the strongest source of
 * internal links on the site, so it features the guides that the 2026-10-19
 * link report (scripts/report-internal-links.ts) found linked from the fewest
 * other pages, alongside one of the most-read. The ATS-friendly guide is
 * linked from the sentence above this list, so it is not repeated here.
 */
export const FEATURED_GUIDES: { slug: string; h1: string; description: string }[] = [
  {
    slug: 'resume-bullet-points-examples',
    h1: 'How to Write Resume Bullet Points',
    description:
      'How to structure a resume bullet, choose a verb that matches your part in the work, fix passive phrasing, and decide how many bullets each job needs.',
  },
  {
    slug: 'resume-checklist',
    h1: 'The Pre-Submission Resume Checklist',
    description:
      'A full printable resume checklist grouped by section, then which items the free tools on this site check for you and which only you can check.',
  },
  {
    slug: 'resume-length',
    h1: 'Resume Length: One Page or Two?',
    description:
      'How long your resume should be for your career stage, what to cut first when you need to lose space, and what to keep.',
  },
  {
    slug: 'resume-file-format-pdf-vs-word',
    h1: 'PDF vs Word: Which Resume File Format to Use',
    description:
      'When to send a PDF, when a Word file is the better choice, how to check that your PDF contains real text, and how to name the file.',
  },
  {
    slug: 'fresher-resume-guide',
    h1: 'How to Write a Resume With No Work Experience',
    description:
      'How to write your first resume as a student, fresher or internship applicant: which sections to include, what counts as experience, and how to use projects.',
  },
  {
    slug: 'resume-projects-section',
    h1: 'How to Write the Projects Section',
    description:
      'When a projects section helps, how to structure each entry, and how to describe a project so a reader can see what you are able to do.',
  },
]
